import {
    Alert,
    AuthPagePresetFrame,
    Button,
    ForgotPasswordLink,
    Form,
    InputLabel,
    showPasswordAddon,
    SignInPageFormValues,
    TextField,
    Toggle,
    toggleLabel
} from "peggirkit";
import React, {useState} from "react";
import {useForm} from "react-hook-form";
import {FormattedMessage, useIntl} from "react-intl";
import {authPageProps} from "@widgets/auth/AuthenticationPage";
import router from "@/routing/Router";
import {useMutation} from "@tanstack/react-query";
import {getHomeHref} from "@/routing/homeRoutes";
import useAuthState from "@/context/authentication/use-auth-state";
import {useLocation} from "react-router";
import {useNavigate} from "react-router-dom";
import {User} from "@/data/entities/user";
import {api} from "@/data/api";

export const rememberMeLocalCookieName = "eataliano-admin-session-auth-user";

const SignIn = () => {
    const intl = useIntl();
    const {setUser} = useAuthState();
    const navigate = useNavigate();
    const location = useLocation();
    const rememberedUsername: string | null = localStorage.getItem(rememberMeLocalCookieName);
    const {register, handleSubmit, formState: {errors}} = useForm<SignInPageFormValues>({
        defaultValues: {
            username: rememberedUsername === null ? undefined : rememberedUsername,
            rememberMe: rememberedUsername !== null,
        },
    });
    const [showPassword, setShowPassword] = useState(false);
    const [showError, setShowError] = useState(false);

    const {mutate, isPending} = useMutation({
        mutationFn: async (form: SignInPageFormValues) => api.auth.signIn(form.username, form.password),
        onSuccess: (user: User) => {
            setUser(user);
            // eslint-disable-next-line  @typescript-eslint/no-unsafe-assignment, @typescript-eslint/no-unsafe-member-access
            const from: Location | undefined = location.state?.from;
            void navigate(
                from ?? getHomeHref(user),
                {replace: true},
            );
        },
        onError: () => setShowError(true),
    });

    return (
        <AuthPagePresetFrame
            {...authPageProps("signIn", intl)}
            title={intl.formatMessage({id: "signIn"})}
        >
            {() => (
                <>
                    <Form
                        onSubmit={(e) => {
                            void handleSubmit((data: SignInPageFormValues) => {
                                setShowError(false);
                                mutate(data);
                            })(e);
                        }}
                        alert={
                            showError
                                ? <Alert
                                    type={"danger"}
                                    title={<FormattedMessage id={"invalidEmailOrPassword"}/>}
                                />
                                : undefined
                        }
                    >
                        <div className={"w-full flex flex-col gap-6"}>
                            <TextField
                                type={"email"}
                                label={
                                    <InputLabel>
                                        <FormattedMessage id={"emailAddress"}/>
                                    </InputLabel>
                                }
                                error={errors.username && errors.username.message}
                                disabled={isPending}
                                {...register("username", {
                                    required: intl.formatMessage({id: "enterAnEmailAddress"}),
                                })}
                            />

                            <TextField
                                type={showPassword ? "text" : "password"}
                                label={
                                    <InputLabel
                                        hint={
                                            <ForgotPasswordLink
                                                href={router.reset.absolutePath()}
                                            >
                                                <FormattedMessage id={"forgotPassword"}/>
                                            </ForgotPasswordLink>
                                        }
                                    >
                                        <FormattedMessage id={"password"}/>
                                    </InputLabel>
                                }
                                paddingRight={0}
                                trailing={
                                    showPasswordAddon(
                                        showPassword,
                                        setShowPassword,
                                        visible => intl.formatMessage({id: visible ? "hidePassword" : "showPassword"}),
                                    )
                                }
                                error={errors.password && errors.password.message}
                                disabled={isPending}
                                {...register("password", {
                                    required: intl.formatMessage({id: "enterYourPassword"}),
                                })}
                            />

                            <Toggle
                                type={"checkbox"}
                                label={toggleLabel(intl.formatMessage({id: "rememberMe"}))}
                                disabled={isPending}
                                {...register("rememberMe")}
                            />
                        </div>

                        <Button
                            width={"w-full"}
                            buttonAction={{as: "button"}}
                            align={"center"}
                            loading={isPending}
                        >
                            <FormattedMessage id={"signIn"}/>
                        </Button>
                    </Form>
                </>
            )}
        </AuthPagePresetFrame>
    );
};

export default SignIn;